import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { toastr } from 'react-redux-toastr';
import { PageContainer } from 'components';
import { Pagination } from 'components/extro/Pagination';
import {
  useDownloadDocumentMutation,
  useDownloadStatemantOfEntryMutation,
  useGetInboxDocumentsQuery,
} from 'store/inbox/inboxApi';
import { fileDownload, formatDate } from 'core';

import { SelectField, TextField, DateField, LoadingCard } from '@britishcouncil/react-solas-ors3';

import { DocumentsList } from './components/document-list';

const S = {
  StyledPagination: styled(Pagination)`
    padding-top: 25px;
  `,
  StyledPageContainer: styled.div`
    &&& {
      div[data-testid='page-container'] {
        padding: 0px;
        background-color: inherit;
      }
    }
  `,
  SessionAndCandidate: styled.div`
    display: flex;
    gap: 20px;
    width: 100%;

    & > div {
      width: 50%;
    }
  `,
  InputWrapper: styled.div`
    display: flex;
    border-radius: 16px;
    border: 2px solid #f4f4f4;
    padding: 20px;
    gap: 10px;
    flex-wrap: wrap;
  `,
  StyledDateField: styled.div`
    width: 100%;

    div.form-group {
      width: 100%;
    }
  `,
  Header: styled.div`
    font-family: BCSansHeading, Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 32px;
    line-height: 53px;
    color: #23085a;
    position: relative;
    margin-bottom: 50px;

    &::after {
      content: '';
      width: 58px;
      height: 0px;
      border: 3px solid #23085a;
      border-radius: 12px;
      left: 0px;
      bottom: -20px;
      position: absolute;
    }
  `,
};

const pageSize = 4;
const clearLabel = 'Clear';

export const Inbox = () => {
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [filterValue, setFilterValue] = useState<{
    sessionName: string | null;
    dayDate: string | null;
    candidateName: string | null;
  }>({ dayDate: null, sessionName: null, candidateName: null });

  const { data: allDocuments } = useGetInboxDocumentsQuery();
  const [downloadDocumentFile, { isLoading: downloadDocumentFileLoading, data: downloadDocumentData }] =
    useDownloadDocumentMutation();

  const [
    downloadStatementOfEntryFile,
    { isLoading: downloadStatementOfEntryFileLoading, data: downloadStatementOfEntyData },
  ] = useDownloadStatemantOfEntryMutation();

  const filteredDocuments = useMemo(
    () =>
      [...(allDocuments || [])]
        .filter((document) =>
          filterValue.candidateName
            ? document.candidateFullName.toLowerCase().startsWith(filterValue.candidateName.toLowerCase())
            : true
        )
        .filter((document) => (filterValue.sessionName ? document.sessionName === filterValue.sessionName : true))
        .filter((document) =>
          filterValue.dayDate ? formatDate(document.notificationSentOn) === formatDate(filterValue.dayDate) : true
        )
        .sort((left, right) => {
          const leftDate = new Date(formatDate(left.notificationSentOn));
          const rightDate = new Date(formatDate(right.notificationSentOn));

          return rightDate.getTime() - leftDate.getTime();
        }),
    [allDocuments, filterValue.candidateName, filterValue.dayDate, filterValue.sessionName]
  );

  useEffect(() => {
    if (downloadDocumentData) {
      fileDownload(downloadDocumentData.file, downloadDocumentData.headers);
    }
  }, [downloadDocumentData]);

  useEffect(() => {
    if (downloadStatementOfEntyData) {
      fileDownload(downloadStatementOfEntyData.file, downloadStatementOfEntyData.headers);
    }
  }, [downloadStatementOfEntyData]);

  if (allDocuments === undefined) {
    return <LoadingCard />;
  }

  return (
    <>
      <S.StyledPageContainer>
        <PageContainer>
          <div>
            <S.Header>Inbox</S.Header>
          </div>

          <S.InputWrapper>
            <S.SessionAndCandidate>
              <SelectField
                label="Session"
                options={[
                  ...[...Array.from(new Set(allDocuments.map((document) => document.sessionName)))].map(
                    (documentSessionName) => ({
                      id: documentSessionName,
                      name: documentSessionName,
                    })
                  ),
                  { id: clearLabel, name: clearLabel },
                ]}
                onChange={(option) => {
                  setFilterValue((currentFilters) => ({
                    ...currentFilters,
                    sessionName: option !== clearLabel ? option : null,
                  }));
                }}
                error=""
                onBlur={() => {}}
                touched={false}
                name="session"
                value={filterValue.sessionName ? filterValue.sessionName : ''}
              />
              <TextField
                label="Candidate"
                error=""
                onBlur={() => {}}
                touched={false}
                name="candidate"
                onChange={(option) => {
                  setFilterValue((currentFilters) => ({
                    ...currentFilters,
                    candidateName: option ?? null,
                  }));
                }}
                value={filterValue.candidateName ? filterValue.candidateName : ''}
              />
            </S.SessionAndCandidate>

            <S.StyledDateField>
              <DateField
                label="Notification sent on"
                mode="general"
                error=""
                onBlur={() => {}}
                touched={false}
                name="date"
                value={filterValue.dayDate ? filterValue.dayDate : ''}
                onChange={(value) => {
                  setFilterValue((currentFilters) => ({
                    ...currentFilters,
                    dayDate: value ?? null,
                  }));
                }}
              />
            </S.StyledDateField>
          </S.InputWrapper>

          <DocumentsList
            documents={filteredDocuments.slice(currentPageIndex * pageSize, currentPageIndex * pageSize + pageSize)}
            isDocumentDownloading={downloadDocumentFileLoading || downloadStatementOfEntryFileLoading}
            onDownload={(document) => {
              if (document.messageType === 'Document')
                downloadDocumentFile({
                  documentId: document.id,
                  candidateId: document.candidateId,
                });
              else if (document.messageType === 'StatementOfEntry')
                downloadStatementOfEntryFile({
                  statementOfEntryId: document.id,
                  candidateId: document.candidateId,
                });
              else toastr.error('Unsupported message type', '');
            }}
          />

          <S.StyledPagination
            pageCount={Math.ceil(filteredDocuments.length / pageSize)}
            pageIndex={currentPageIndex}
            onPageIndexChange={(pageIndex) => {
              setCurrentPageIndex(pageIndex);
            }}
          />
        </PageContainer>
      </S.StyledPageContainer>
    </>
  );
};
